// Workspace settings (FD §8). The approval policy is editable; the rest is
// a read-only summary for now.
//
// TODO: editable forms for defaultBudgets, retentionPolicy,
// allowedExecutorTypes and defaultNetworkPolicy, gated to
// workspace_owner/administrator (FD §7.1, §8.1).

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { Workspace } from '@goblic/shared'
import { ApiError, meApi, workspaceApi } from '../lib/api-client'
import { useDialogs } from '../components/dialogs'
import { GoblicMcpPanel } from '../components/goblic-mcp-panel'
import { LeaveOrDeletePanel, MembersPanel } from '../components/members-panel'
import { WebSearchPanel } from '../components/web-search-panel'
import { THEME_OPTIONS, useTheme, type ThemeChoice } from '../lib/theme'
import { useIllustrations } from '../lib/illustrations'
import { ClayIcon, type ClayIconName } from '../components/goblin-art'
import {
  ErrorNote,
  Facts,
  Field,
  LoadingRows,
  PageHeader,
  Panel,
} from '../components/ui'
import { useAuth, useWorkspaceId } from '../lib/auth'
import { formatMoney, formatMs, humanize } from '../lib/format'

/** Personal look of the app; stored in this browser, not on the workspace. */
function AppearancePanel() {
  const [theme, setTheme] = useTheme()
  const [illustrations, setIllustrations] = useIllustrations()
  return (
    <Panel
      title="Appearance"
      description="Just for you, in this browser."
      flush
    >
      <ul className="divide-y divide-line">
        <li className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <span className="min-w-0">
            <span className="block text-base font-medium text-ink">Theme</span>
            <span className="block text-sm text-ink-3">
              Match system follows your computer’s light or dark setting.
            </span>
          </span>
          <div
            role="group"
            aria-label="Theme"
            className="inline-flex shrink-0 rounded-control border border-line-strong bg-sunken p-0.5"
          >
            {THEME_OPTIONS.map((option) => {
              const active = option.value === theme
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTheme(option.value as ThemeChoice)}
                  className={`rounded-[6px] px-3 py-1 text-sm font-medium transition-colors ${
                    active
                      ? 'bg-surface text-ink shadow-sm'
                      : 'text-ink-3 hover:text-ink'
                  }`}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        </li>
        <li className="flex items-center justify-between gap-4 px-5 py-3">
          <span className="min-w-0">
            <span className="block text-base font-medium text-ink">
              Welcome tour
            </span>
            <span className="block text-sm text-ink-3">
              How Goblic works, and the first things to set up.
            </span>
          </span>
          <Link to="/welcome" className="btn-secondary btn-sm shrink-0">
            Open the welcome
          </Link>
        </li>
        <li className="flex items-center justify-between gap-4 px-5 py-3">
          <label htmlFor="illustrations-switch" className="min-w-0">
            <span className="block text-base font-medium text-ink">
              Goblin illustrations
            </span>
            <span className="block text-sm text-ink-3">
              The goblins, icons and scenes. Off gives a plain interface.
            </span>
          </label>
          <span className="flex shrink-0 items-center gap-2.5">
            <span className="text-sm text-ink-2">
              {illustrations ? 'On' : 'Off'}
            </span>
            <input
              id="illustrations-switch"
              type="checkbox"
              role="switch"
              className="switch"
              checked={illustrations}
              onChange={(event) => setIllustrations(event.target.checked)}
            />
          </span>
        </li>
      </ul>
    </Panel>
  )
}

/** "8 hours", "1 day", "30 days". */
function formatSignInDuration(seconds: number): string {
  if (seconds % 86_400 === 0) {
    const days = seconds / 86_400
    return days === 1 ? '1 day' : `${days} days`
  }
  const hours = Math.round(seconds / 3600)
  return hours === 1 ? '1 hour' : `${hours} hours`
}

/** How long you stay signed in (issue #3); stored on your account, used from the next sign-in. */
function SignInPanel() {
  const queryClient = useQueryClient()
  const { alert, confirm } = useDialogs()
  const settingsQuery = useQuery({
    queryKey: ['me', 'sign-in'],
    queryFn: () => meApi.getSignIn(),
  })
  const save = useMutation({
    mutationFn: (seconds: number | null) => meApi.setSignIn(seconds),
    onSuccess: (data) => queryClient.setQueryData(['me', 'sign-in'], data),
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t save how long you stay signed in',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })
  const revokeOthers = useMutation({
    mutationFn: () => meApi.revokeOtherSessions(),
    onSuccess: ({ revoked }) =>
      void alert({
        kind: 'success',
        title:
          revoked === 0
            ? 'No other sessions were signed in'
            : revoked === 1
              ? 'Signed out 1 other session'
              : `Signed out ${revoked} other sessions`,
      }),
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t sign out your other sessions',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })

  async function confirmRevokeOthers(): Promise<void> {
    const ok = await confirm({
      kind: 'warning',
      title: 'Sign out everywhere else?',
      message:
        'Every other browser or device signed in to your account has to sign in again. This one stays signed in.',
      confirmLabel: 'Sign out other sessions',
    })
    if (ok) revokeOthers.mutate()
  }

  const settings = settingsQuery.data
  return (
    <Panel
      title="Sign-in"
      description="For your account, on every device."
      flush
    >
      {settingsQuery.isLoading ? (
        <LoadingRows />
      ) : settingsQuery.isError || !settings ? (
        <ErrorNote error={settingsQuery.error} what="sign-in settings" />
      ) : (
        <ul className="divide-y divide-line">
          <li className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <label htmlFor="sign-in-duration" className="min-w-0">
              <span className="block text-base font-medium text-ink">
                Stay signed in for
              </span>
              <span className="block text-sm text-ink-3">
                Applies from your next sign-in.
                {settings.options.some((option) => !option.allowed)
                  ? ` This installation allows at most ${formatSignInDuration(settings.maxSeconds)}.`
                  : ''}
              </span>
            </label>
            <select
              id="sign-in-duration"
              className="field-input w-auto shrink-0"
              value={settings.sessionDurationSeconds ?? ''}
              disabled={save.isPending}
              onChange={(event) =>
                save.mutate(
                  event.target.value === '' ? null : Number(event.target.value)
                )
              }
            >
              <option value="">
                Default ({formatSignInDuration(settings.defaultSeconds)})
              </option>
              {settings.options.map((option) => (
                <option
                  key={option.seconds}
                  value={option.seconds}
                  disabled={
                    !option.allowed &&
                    option.seconds !== settings.sessionDurationSeconds
                  }
                >
                  {formatSignInDuration(option.seconds)}
                  {option.allowed ? '' : ' (above the maximum)'}
                </option>
              ))}
            </select>
          </li>
          <li className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <span className="min-w-0">
              <span className="block text-base font-medium text-ink">
                Other sessions
              </span>
              <span className="block text-sm text-ink-3">
                Signed in somewhere you no longer use? Sign out everywhere but here.
              </span>
            </span>
            <button
              type="button"
              className="btn-secondary btn-sm shrink-0"
              disabled={revokeOthers.isPending}
              onClick={() => void confirmRevokeOthers()}
            >
              Sign out other sessions
            </button>
          </li>
        </ul>
      )}
    </Panel>
  )
}

type ApprovalPolicy = NonNullable<Workspace['defaultApprovalPolicy']>
type ApprovalKey = Exclude<keyof ApprovalPolicy, 'defaultExpiryMs'>

/** In the order a run meets them. */
const APPROVAL_ROWS: {
  key: ApprovalKey
  icon: ClayIconName
  label: string
  hint: string
}[] = [
  {
    key: 'requireApprovalForPrivilegedTools',
    icon: 'key',
    label: 'Privileged tools',
    hint: 'Tools that act with elevated rights.',
  },
  {
    key: 'requireApprovalForDestructiveTools',
    icon: 'bin',
    label: 'Destructive tools',
    hint: 'Tools that delete or overwrite.',
  },
  {
    key: 'requireApprovalForBranchPush',
    icon: 'push',
    label: 'Pushing a branch',
    hint: 'After the agent is done, before its branch goes to GitHub.',
  },
  {
    key: 'requireApprovalForPullRequest',
    icon: 'pull-request',
    label: 'Opening a pull request',
    hint: 'Before the draft pull request is opened.',
  },
  {
    key: 'requireApprovalForDeploymentMergeRelease',
    icon: 'rocket',
    label: 'Merge, deploy or release',
    hint: 'Anything that reaches production.',
  },
]

function ApprovalPolicyPanel({
  workspaceId,
  policy,
}: {
  workspaceId: string
  policy: Workspace['defaultApprovalPolicy']
}) {
  const queryClient = useQueryClient()
  const { alert } = useDialogs()
  const current: Record<ApprovalKey, boolean> = {
    requireApprovalForPrivilegedTools:
      policy?.requireApprovalForPrivilegedTools ?? false,
    requireApprovalForDestructiveTools:
      policy?.requireApprovalForDestructiveTools ?? false,
    requireApprovalForBranchPush: policy?.requireApprovalForBranchPush ?? false,
    requireApprovalForPullRequest:
      policy?.requireApprovalForPullRequest ?? false,
    requireApprovalForDeploymentMergeRelease:
      policy?.requireApprovalForDeploymentMergeRelease ?? false,
  }
  const save = useMutation({
    mutationFn: (next: Record<ApprovalKey, boolean>) =>
      workspaceApi.update(workspaceId, {
        defaultApprovalPolicy: {
          ...next,
          ...(policy?.defaultExpiryMs
            ? { defaultExpiryMs: policy.defaultExpiryMs }
            : {}),
        },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] }),
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t save the approval setting',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })

  return (
    <Panel
      title="Approvals"
      description="Which actions pause a run until someone approves."
      flush
    >
      <ul className="divide-y divide-line">
        {APPROVAL_ROWS.map((row) => {
          const id = `approval-${row.key}`
          return (
            <li
              key={row.key}
              className="flex items-center justify-between gap-4 px-5 py-3"
            >
              <label htmlFor={id} className="flex min-w-0 items-center gap-3">
                <ClayIcon name={row.icon} size={32} />
                <span className="min-w-0">
                  <span className="block text-base font-medium text-ink">
                    {row.label}
                  </span>
                  <span className="block text-sm text-ink-3">{row.hint}</span>
                </span>
              </label>
              <span className="flex shrink-0 items-center gap-2.5">
                <span className="text-sm text-ink-2">
                  {current[row.key] ? 'Needs approval' : 'Allowed'}
                </span>
                <input
                  id={id}
                  type="checkbox"
                  role="switch"
                  className="switch"
                  checked={current[row.key]}
                  disabled={save.isPending}
                  onChange={(event) =>
                    save.mutate({ ...current, [row.key]: event.target.checked })
                  }
                />
              </span>
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}

const RUN_COST_ROWS: {
  value: NonNullable<Workspace['runCostDisplay']>
  label: string
  hint: string
}[] = [
  {
    value: 'estimate',
    label: 'Show as an estimate',
    hint: 'A subscription run’s cost shows the API-price estimate, e.g. “~$0.16”.',
  },
  {
    value: 'included',
    label: 'Show as included',
    hint: 'A subscription run’s cost shows “Included” instead, with the estimate in a tooltip.',
  },
]

function RunCostPanel({
  workspaceId,
  runCostDisplay,
}: {
  workspaceId: string
  runCostDisplay: Workspace['runCostDisplay']
}) {
  const queryClient = useQueryClient()
  const { alert } = useDialogs()
  const current = runCostDisplay ?? 'estimate'
  const save = useMutation({
    mutationFn: (next: NonNullable<Workspace['runCostDisplay']>) =>
      workspaceApi.update(workspaceId, { runCostDisplay: next }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] }),
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t save the run cost setting',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })

  return (
    <Panel
      title="Run costs"
      description="How a run on a subscription login (Claude Code, Codex or Pi) shows its cost. Nothing is ever billed per run either way."
      flush
    >
      <ul className="divide-y divide-line">
        {RUN_COST_ROWS.map((row) => {
          const id = `run-cost-${row.value}`
          return (
            <li
              key={row.value}
              className="flex items-center justify-between gap-4 px-5 py-3"
            >
              <label htmlFor={id} className="flex min-w-0 items-center gap-3">
                <span className="min-w-0">
                  <span className="block text-base font-medium text-ink">
                    {row.label}
                  </span>
                  <span className="block text-sm text-ink-3">{row.hint}</span>
                </span>
              </label>
              <input
                id={id}
                type="radio"
                name="run-cost-display"
                className="shrink-0"
                checked={current === row.value}
                disabled={save.isPending}
                onChange={() => save.mutate(row.value)}
              />
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}

/** Workspace name, editable for owners and administrators. */
function WorkspaceNameForm({ workspace }: { workspace: Workspace }) {
  const queryClient = useQueryClient()
  const { alert } = useDialogs()
  const [name, setName] = useState(workspace.name)
  const save = useMutation({
    mutationFn: (next: string) => workspaceApi.update(workspace.id, { name: next }),
    onSuccess: async (updated) => {
      setName(updated.name)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['workspace', workspace.id] }),
        queryClient.invalidateQueries({ queryKey: ['workspaces', 'mine'] }),
      ])
    },
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t rename the workspace',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })
  const trimmed = name.trim()
  const dirty = trimmed !== '' && trimmed !== workspace.name

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (dirty) save.mutate(trimmed)
  }

  return (
    <form className="mb-4 flex items-end gap-2" onSubmit={handleSubmit}>
      <div className="min-w-0 flex-1">
        <Field label="Name" htmlFor="workspace-name">
          <input
            id="workspace-name"
            className="field-input"
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
      </div>
      <button type="submit" className="btn-primary" disabled={!dirty || save.isPending}>
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
    </form>
  )
}

export function SettingsPage() {
  const { user } = useAuth()
  const canRename =
    user?.roles.some((r) => r === 'workspace_owner' || r === 'administrator') ?? false
  const workspaceId = useWorkspaceId()
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => workspaceApi.get(workspaceId),
    enabled: workspaceId !== '',
  })
  const workspace = workspaceQuery.data

  return (
    <div>
      <PageHeader
        title="Settings"
        description="How Goblic looks for you, and the defaults for every project in this workspace."
      />
      {workspaceQuery.isLoading ? (
        <div className="panel">
          <LoadingRows />
        </div>
      ) : workspaceQuery.isError || !workspace ? (
        <div className="panel">
          <ErrorNote error={workspaceQuery.error} what="workspace settings" />
        </div>
      ) : (
        <div className="max-w-3xl space-y-6">
          <AppearancePanel />
          <SignInPanel />
          <Panel title="General">
            {canRename ? <WorkspaceNameForm key={workspace.name} workspace={workspace} /> : null}
            <Facts
              items={[
                ...(canRename ? [] : [{ label: 'Name', value: workspace.name }]),
                { label: 'Time zone', value: workspace.defaultTimezone },
                {
                  label: 'Executors allowed',
                  value:
                    workspace.allowedExecutorTypes.map(humanize).join(', ') ||
                    'None',
                },
              ]}
            />
          </Panel>
          <Panel title="Default limits per run">
            <Facts
              columns={3}
              items={[
                {
                  label: 'Cost',
                  value: formatMoney(workspace.defaultBudgets?.maxCostCents),
                },
                {
                  label: 'Duration',
                  value: formatMs(workspace.defaultBudgets?.maxDurationMs),
                },
                {
                  label: 'Tokens',
                  value:
                    workspace.defaultBudgets?.maxModelTokens?.toLocaleString() ??
                    '—',
                },
              ]}
            />
          </Panel>
          <ApprovalPolicyPanel
            workspaceId={workspaceId}
            policy={workspace.defaultApprovalPolicy}
          />
          <RunCostPanel
            workspaceId={workspaceId}
            runCostDisplay={workspace.runCostDisplay}
          />
          <GoblicMcpPanel
            workspaceId={workspaceId}
            externalApiAccess={workspace.externalApiAccess ?? false}
          />
          <WebSearchPanel workspaceId={workspaceId} />
          <MembersPanel workspaceId={workspaceId} />
          <Panel title="Network and retention">
            <Facts
              items={[
                {
                  label: 'Network access',
                  value: workspace.defaultNetworkPolicy.defaultDenyAll
                    ? 'Blocked unless allowed'
                    : 'Open by default',
                },
                {
                  label: 'Allowed domains',
                  value:
                    workspace.defaultNetworkPolicy.allowedDomains?.join(', ') ||
                    'None',
                },
                {
                  label: 'Keep runs for',
                  value:
                    workspace.retentionPolicy.runRetentionDays != null
                      ? `${workspace.retentionPolicy.runRetentionDays} days`
                      : 'Forever',
                },
                {
                  label: 'Keep audit log for',
                  value:
                    workspace.retentionPolicy.auditLogRetentionDays != null
                      ? `${workspace.retentionPolicy.auditLogRetentionDays} days`
                      : 'Forever',
                },
              ]}
            />
          </Panel>
          <LeaveOrDeletePanel workspaceId={workspaceId} workspaceName={workspace.name} />
        </div>
      )}
    </div>
  )
}
